import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import moment from 'moment';
import cloneDeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import ArtistSelector from "../artist-selector/";
import FeatureCategorySelector from "../feature-category-selector/";
import AddFeatureHeader from './add-feature-header';
import DatePicker from '../date-picker/';
import ImageUploader from '../image-uploader/image-uploader';
import InputField from '../input-field';
import LoadingAnimation from '../loading-animation';
import Editor from '../editor/';
import { UserContext } from "../../providers/user-provider";
import { generateId } from '../../utilities/';
import * as Actions from '../../redux/actions/';
import { addFeature, getFeature } from "../../api/";
import { dateToTimestamp } from '../../firebase';
import { fromString } from 'html-to-text';
import './add-feature.scss';
const defaultFeatureState = {
	artist: null,
	description: '',
	id: null,
	image: null,
	isLegacy: false,
	name: '',
	publishDate: null,
	author: null,
	status: 'draft',
}
// TODO: Split this up into smaller components. Refactor useState to useReducer
const AddFeature = ({ addNotification, featureCategories, featureData, features, getCollectionSubscription, impersonationUser, match, mode, onSubmit, onExit, saveFeature }) => {
	const featureEditId = match && match.params && match.params.slug;
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [lastSaveTime, setLastSaveTime] = useState(null);
	const userContext = useContext(UserContext);
	const user = impersonationUser || userContext;
	const [featureState, setFeatureState] = useState(defaultFeatureState);

	function updateFeatureState(newState) {
		setFeatureState(prevState => {
			const newVal = {
	        ...prevState,
	        ...newState
	    };
			return newVal;
		});
	};

	useEffect(() => {
		async function getFeatureData() {
			let thisFeature;

			if (featureEditId) {
				// if the feature is already in the store use that, otherwise fetch it'
				thisFeature = features.find(feature => feature.id === featureEditId);

				setIsLoading(true);
				if (!thisFeature) {
					try {
						thisFeature = await getFeature(featureEditId);
					} catch {
						setIsLoading(false);
						return;
					}
				}

				setFeatureState(thisFeature);
			} else if (featureData) {
				setFeatureState(featureData);
			}
			setIsLoading(false);
		}
		getFeatureData();
	}, [featureData, featureEditId, features, getCollectionSubscription]);

	function handleArtistSelected (artist) {
		updateFeatureState({artist: artist});
	}

	function handleArtistDeselected () {
		updateFeatureState({name: ''});
	}

	function handleSubCategorySelected (subCategory) {
		updateFeatureState({category: subCategory});
	}

	function handleSubCategoryDeselected () {
		updateFeatureState({category: null});
	}

	function handleFeatureCategorySelected (featureCategory) {
		updateFeatureState({type: featureCategory});
	}

	function handleFeatureCategoryDeselected () {
		updateFeatureState({type: null});
	}

	function handleEditorChange (content) {
		updateFeatureState({content: content});
	}

	const handleUploadSuccess = (filename) => {
		updateFeatureState({image: filename});
	}

	const handleUploadError = (error) => {
		console.log('error', error);
	}

	const handleDateChange = (val) => {
		if (val !== null) {
			let changeObj;
			if (featureState.isLegacy) {
				changeObj = {
					legacyDate: val,
					publishDate: dateToTimestamp(new Date())
				}
			} else {
				changeObj = { publishDate: val }
			}
			updateFeatureState(changeObj);
		}
	};

	function featureDataLocked () {
		return featureState.status === 'approved' && userContext.role !== 'owner';
	}

	async function handleSave(featureStatus) {
		let newData = cloneDeep(featureState);
		const plainTextOptions = {
			ignoreHref: true,
			ignoreImage: true,
			wordwrap: null,
			format: {
				heading: () => {
					return '';
				}
			}
		}
		const plainText = `${fromString(featureState.content, plainTextOptions).slice(0, 195).trim()}...`;

		newData.id = newData.id || generateId(user.id, newData.name);
		newData.author = featureState.author || user.name;
		newData.authorId = featureState.authorId || user.id;
		newData.authorPhoto = featureState.authorPhoto === null ? null : (featureState.authorPhoto || user.photoURL);
		newData.status = featureStatus;
		newData.previewText = plainText;

		setIsSaving(true);

		addFeature(newData).then(() => {
			let message;
			if (featureStatus === 'pending') {
				message = `Your feature, ${featureState.name} has been submitted for approval.`;
			} else {
				message = `Successfully saved your feature, ${featureState.name} `;
			}
			saveFeature(newData);
			setIsSaving(false);
			setLastSaveTime(moment().format('LT'));
			updateFeatureState(newData);
			addNotification({message: message});
		});
	};

	const renderForm = () => {
		return (
			<div className="admin-app-page">
				{isSaving && !isLoading && <LoadingAnimation text="Saving..." showMask />}
				{isLoading && <LoadingAnimation text="One moment, please..." showMask /> }
					<>
						<AddFeatureHeader
							featureData={featureState}
							lastSaveTime={lastSaveTime}
							onSave={handleSave}
						/>
							<div className="feature-form col-layout layout-30-70">
								<div className="feature-fields-1">
									<FeatureCategorySelector
										featureCategoryData={ featureState.type }
										subCategoryData= { featureState.category }
										disabled={featureDataLocked()}
										label="Feature Type:"
										onDeselect={ handleFeatureCategoryDeselected }
										onSelect={ handleFeatureCategorySelected }
										onSubCategoryDeselect={ handleSubCategoryDeselected }
										onSubCategorySelect={ handleSubCategorySelected }
										allowAdd
									/>
									{userContext.role === 'owner' &&
										<InputField
											id="isLegacy"
											label="Legacy Review"
											onChange={(evt) => {
												updateFeatureState({isLegacy: evt.target.checked});
											}}
											checked={featureState.isLegacy ? ['Yes'] : []}
											disabled={[]}
											type="checkbox"
											value={['Yes']}
										/>
									}
									<ImageUploader
										label="Primary Image"
										onUploadSuccess={handleUploadSuccess}
										onUploadError={handleUploadError}
										value={featureState.image}
										disabled={featureDataLocked()}
									 />
									{ featureDataLocked() && featureData.artist ?
										<InputField
											disabled
											id="artist"
											label="Associated Artist"
											value={featureState.artist.name || ''}
											onChange={(evt) => {
												const artistState = featureState.artist;
												const newState = {name: evt.target.value}
												updateFeatureState({ artist: {...artistState, ...newState} });
											}}
										/> :
										<ArtistSelector
											artistData={ featureState.artist }
											disabled={featureDataLocked()}
											label="Associated Artist (optional)"
											onDeselect={ handleArtistDeselected }
											onSelect={ handleArtistSelected }
											allowAdd
										/>
									}
									<DatePicker
										disabled={featureDataLocked()}
										id="release-date-picker"
										label="Publish Date"
										onChange={handleDateChange}
										value={featureState.publishDate}
									/>
									<InputField
										disabled={featureDataLocked()}
										id="description"
										label="Description"
										maxLength="140"
										onChange={(evt) => {
											updateFeatureState({description: evt.target.value});
										}}
										placeholder="This will display in home page callouts"
										type="textarea"
										value={featureState.description}
									/>
								</div>
								<div className="editor-wrap">
									<InputField
										disabled={featureDataLocked()}
										id="name"
										label="Feature Title"
										maxLength="140"
										onChange={(evt) => {
											updateFeatureState({name: evt.target.value});
										}}
										value={featureState.name}
									/>
									<div className="form-element">
										<h2>Feature Content</h2>
										<Editor
											context="add-feature"
											data={featureState.content || ''}
											onInit={() => {
											}}
											onChange={debounce(content => { handleEditorChange(content); }, 1600)}
											disabled={featureDataLocked()}
										/>
									</div>
								</div>
						</div>
				</>
			</div>
		);
	};

	const renderPage = () => {
		return renderForm();
	};

	return renderPage();
};

AddFeature.propTypes = {
	impersonationUser: PropTypes.object,
	onSubmit: PropTypes.func
}

const mapStateToProps = state => {
	return {
		features: state.features,
		featureCategories: state.genres,
		impersonationUser: state.impersonationUser
  }
}

const mapDispatchToProps = {
	addNotification: Actions.addNotification,
	saveFeature: Actions.saveFeature
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps)
)(AddFeature)
